/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useContext, useEffect, useMemo, useState } from "react";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined
} from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Input,
  message,
  Modal,
  Space,
  Typography
} from "antd";
import type { MenuProps } from "antd";
import { QUERY_GET_ALL_LOCATIONS } from "services/graphQL/queries";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { ProjectContext } from "context/ProjectProvider";
import { cloneDeep } from "lodash";
import { useMutation } from "@apollo/client";
import { isGcProjectAdminRole } from "constants/index";
import {
  DELETE_LOCATION,
  INSERT_LOCATION,
  RENAME_LOCATION,
  UPDATE_LOCATION
} from "./mutations";
import "./locations.scss";

const { Text } = Typography;

interface TreeNode {
  action?: string;
  id: string;
  name: string;
  occurrences?: number;
  parent_id: string;
  level: number;
  order: number;
}

function collectLocationUpdate(nodes: Array<TreeNode>, editingNodeId: string) {
  const updateObj: any = {
    parent_id: null,
    location_nodes: []
  };
  let parentID = null;
  let order = null;
  let id = null;

  nodes.forEach((node) => {
    if (node.id === editingNodeId) {
      parentID = node.parent_id;
      order = node.order;
      id = node.id;
    }

    if (node?.action === "update") {
      updateObj.parent_id = node.parent_id;
      updateObj.location_nodes.push({
        id: node.id,
        order: node.order,
        name: node.name
      });
    }
  });

  return { updateObj, parentID, order, id };
}

function Locations() {
  const { gqlClientForProject, tokenContents, isProjectArchived } =
    useContext(ProjectContext);
  const { data: locations, refetch } = useCIQQuery(QUERY_GET_ALL_LOCATIONS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [projectId, setProjectId] = useState<string>("");
  const [levels, setLevels] = useState<number[]>([]);
  const [btnDisabled, setBtnDisabled] = useState<boolean>(false);
  const [insertLocation] = useMutation(INSERT_LOCATION, {
    client: gqlClientForProject
  });
  const [renameLocation] = useMutation(RENAME_LOCATION, {
    client: gqlClientForProject
  });
  const [updateLocation] = useMutation(UPDATE_LOCATION, {
    client: gqlClientForProject
  });
  const [deleteLocation] = useMutation(DELETE_LOCATION, {
    client: gqlClientForProject
  });
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const isGcADmin = isGcProjectAdminRole(tokenContents?.role);
  const isPermissionEnabled = isGcADmin && !isProjectArchived;

  function addLevelsRecursive(treeNodes: TreeNode[]): TreeNode[] {
    const nodeMap = new Map<string, TreeNode>(
      treeNodes.map((node) => [node.id, node])
    );

    const processNode = (nodeId: string, level: number = 0): void => {
      const node = nodeMap.get(nodeId);
      if (!node) return;

      node.level = level;

      treeNodes.forEach((n) => {
        if (n.parent_id === nodeId) {
          processNode(n.id, level + 1);
        }
      });
    };

    treeNodes.forEach((node) => {
      if (!node.parent_id) {
        processNode(node.id);
      }
    });

    return treeNodes;
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const treeElement = document.querySelector(".tree-grid");
      if (treeElement && !treeElement.contains(event.target as Node)) {
        setSelectedNodeId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    refetch();
    const uniqueLevels: number[] = Array.from(
      new Set(
        nodes.filter((item) => item.level !== 0).map((item) => item.level)
      )
    ).sort((a, b) => a - b);
    if (uniqueLevels.length > 0) {
      setLevels(uniqueLevels);
    } else {
      setLevels([1]);
    }
  }, [nodes, refetch]);

  useEffect(() => {
    if (!locations) return;
    const locationsData = cloneDeep(locations.location);
    if (locationsData.length > 0) {
      locationsData.forEach((location: any, index: any) => {
        if (location.parent_id === null) {
          locationsData[index].parent_id = "";
          setProjectId(locationsData[index].id);
          setExpandedNodes((prev) => ({
            ...prev,
            [locationsData[index].id]: true
          }));
        }
      });

      const treeNodes: TreeNode[] = locationsData.map((location: any) => ({
        id: location.id,
        name: location.name,
        parent_id: location.parent_id,
        order: location.order,
        level: 0,
        occurrences: location.occurrences
      }));

      const result = addLevelsRecursive(treeNodes);
      setNodes(result);
    }
  }, [locations]);

  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [isNewNode, setIsNewNode] = useState<boolean>(false);

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const handleNodeClick = (nodeId: string) => {
    setSelectedNodeId(nodeId);
  };

  const handleTierClick = (tier: number) => {
    const newExpandedNodes = { ...expandedNodes };
    Object.keys(newExpandedNodes).forEach((key) => {
      newExpandedNodes[key] = false;
    });
    nodes.forEach((node) => {
      if (node.level < tier) {
        newExpandedNodes[node.id] = true;
      }
    });

    setExpandedNodes(newExpandedNodes);
  };

  const canMoveUp = (nodeId: string): boolean => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return false;

    const siblings = nodes.filter((n) => n.parent_id === node.parent_id);

    const sortedSiblings = [...siblings].sort((a, b) => a.order - b.order);

    return sortedSiblings[0]?.id !== nodeId;
  };

  const canMoveDown = (nodeId: string): boolean => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return false;

    const siblings = nodes.filter((n) => n.parent_id === node.parent_id);

    const sortedSiblings = [...siblings].sort((a, b) => a.order - b.order);

    return sortedSiblings[sortedSiblings.length - 1]?.id !== nodeId;
  };

  const handleMoveNode = async (nodeId: string, direction: "up" | "down") => {
    const bufferNodes = cloneDeep(nodes);
    const updateObj: any = {
      parent_id: null,
      location_nodes: []
    };
    const nodeToMove = nodes.find((n) => n.id === nodeId);
    if (!nodeToMove) return;

    const siblings = nodes.filter((n) => n.parent_id === nodeToMove.parent_id);

    const sortedSiblings = [...siblings].sort((a, b) => a.order - b.order);

    const currentIndex = sortedSiblings.findIndex((n) => n.id === nodeId);

    if (direction === "up" && currentIndex > 0) {
      const siblingBefore = sortedSiblings[currentIndex - 1];

      bufferNodes.forEach((node) => {
        if (node.id === nodeId) {
          updateObj.parent_id = node.parent_id;
          updateObj.location_nodes.push({
            id: node.id,
            order: siblingBefore.order,
            name: node.name
          });
        }

        if (node.id === siblingBefore.id) {
          updateObj.parent_id = node.parent_id;
          updateObj.location_nodes.push({
            id: node.id,
            order: nodeToMove.order,
            name: node.name
          });
        }
      });

      if (updateObj.location_nodes.length > 0) {
        const res = await updateLocation({
          variables: updateObj
        });
        if (!res.data) {
          message.error("Something has failed. Try again later.");
          return;
        }
      }

      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id === nodeId) {
            return { ...node, order: siblingBefore.order };
          }
          if (node.id === siblingBefore.id) {
            return { ...node, order: nodeToMove.order };
          }
          return node;
        })
      );
    } else if (
      direction === "down" &&
      currentIndex < sortedSiblings.length - 1
    ) {
      const siblingAfter = sortedSiblings[currentIndex + 1];

      bufferNodes.forEach((node) => {
        if (node.id === nodeId) {
          updateObj.parent_id = node.parent_id;
          updateObj.location_nodes.push({
            id: node.id,
            order: siblingAfter.order,
            name: node.name
          });
        }

        if (node.id === siblingAfter.id) {
          updateObj.parent_id = node.parent_id;
          updateObj.location_nodes.push({
            id: node.id,
            order: nodeToMove.order,
            name: node.name
          });
        }
      });

      if (updateObj.location_nodes.length > 0) {
        const res = await updateLocation({
          variables: updateObj
        });
        if (!res.data) {
          message.error("Something has failed. Try again later.");
          return;
        }
      }

      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id === nodeId) {
            return { ...node, order: siblingAfter.order };
          }
          if (node.id === siblingAfter.id) {
            return { ...node, order: nodeToMove.order };
          }
          return node;
        })
      );
    }
  };

  const handleAddChild = (parentId: string) => {
    const parentNode = nodes.find((node) => node.id === parentId);
    if (!parentNode) return;

    // Get existing children to determine the next order
    const existingChildren = nodes.filter(
      (node) => node.parent_id === parentId
    );
    const nextOrder =
      existingChildren.length > 0
        ? Math.max(...existingChildren.map((child) => child.order)) + 1
        : 1;

    // Generate a new unique ID
    const childrenCount = existingChildren.length;
    const newNodeId = `${parentId}-${childrenCount + 1}`;

    // Create a temporary node for editing
    const newNode: TreeNode = {
      id: newNodeId,
      name: "",
      parent_id: parentId,
      level: parentNode.level + 1,
      order: nextOrder
    };

    // Add the new node to the data
    setNodes((prevNodes) => [...prevNodes, newNode]);

    // Ensure the parent node is expanded
    setExpandedNodes((prev) => ({
      ...prev,
      [parentId]: true
    }));

    // Start editing the new node
    setEditingNodeId(newNodeId);
    setEditingName("");
    setIsNewNode(true);
  };

  const handleAddSibling = (
    siblingId: string,
    position: "before" | "after"
  ) => {
    const siblingNode = nodes.find((node) => node.id === siblingId);
    if (!siblingNode) return;

    const parentId = siblingNode.parent_id;

    const siblings = nodes.filter((node) => node.parent_id === parentId);

    let newOrder: number;
    if (position === "before") {
      newOrder = siblingNode.order;

      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.parent_id === parentId && node.order >= newOrder) {
            return { ...node, order: node.order + 1, action: "update" };
          }
          return node;
        })
      );
    } else {
      newOrder = siblingNode.order + 1;

      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.parent_id === parentId && node.order > siblingNode.order) {
            return { ...node, order: node.order + 1, action: "update" };
          }
          return node;
        })
      );
    }

    const siblingCount = siblings.length;
    const newNodeId = `${parentId}-${siblingCount + 1}`;

    const newNode: TreeNode = {
      id: newNodeId,
      name: "",
      parent_id: parentId,
      level: siblingNode.level,
      order: newOrder
    };

    setNodes((prevNodes) => [...prevNodes, newNode]);

    if (parentId) {
      setExpandedNodes((prev) => ({
        ...prev,
        [parentId]: true
      }));
    }

    setEditingNodeId(newNodeId);
    setEditingName("");
    setIsNewNode(true);
  };

  const handleRemoveNode = async (nodeId: string) => {
    const res = await deleteLocation({
      variables: {
        id: nodeId
      }
    });

    if (res.data) {
      const getDescendantIds = (id: string): string[] => {
        const children = nodes.filter((node) => node.parent_id === id);
        const childIds = children.map((child) => child.id);
        const descendantIds = childIds.flatMap((childId) =>
          getDescendantIds(childId)
        );
        return [...childIds, ...descendantIds];
      };

      const descendantIds = getDescendantIds(nodeId);
      const idsToRemove = [nodeId, ...descendantIds];

      setNodes((prevNodes) =>
        prevNodes.filter((node) => !idsToRemove.includes(node.id))
      );

      setExpandedNodes((prev) => {
        const newState = { ...prev };
        idsToRemove.forEach((id) => {
          delete newState[id];
        });
        return newState;
      });
    }
  };

  const deleteLocations = async (nodeId: string) => {
    Modal.confirm({
      title:
        "Removing will delete this Location from all the places it occurs. Are you sure you want to proceed?",
      okText: "Yes",
      cancelText: "No",
      async onOk() {
        await handleRemoveNode(nodeId);
      }
    });
  };

  const renameLocations = async (nodeId: string, name: string) => {
    Modal.confirm({
      title:
        "Renaming will update the name of this Location in all the places it occurs. Are you sure you want to proceed?",
      okText: "Yes",
      cancelText: "No",
      async onOk() {
        setEditingNodeId(nodeId);
        setEditingName(name);
        setIsNewNode(false);
      }
    });
  };

  const handleMenuAction = (action: string, nodeId: string) => {
    const selectedNode = nodes.find((node) => node.id === nodeId);
    if (!selectedNode) return;

    switch (action) {
      case "rename":
        renameLocations(nodeId, selectedNode.name);
        break;

      case "add-sublocation":
        handleAddChild(nodeId);
        break;

      case "add-before":
        handleAddSibling(nodeId, "before");
        break;

      case "add-after":
        handleAddSibling(nodeId, "after");
        break;

      case "remove":
        deleteLocations(nodeId);
        break;

      case "move-up":
        handleMoveNode(nodeId, "up");
        break;

      case "move-down":
        handleMoveNode(nodeId, "down");
        break;

      default:
        break;
    }
  };

  const handleNameClick = (nodeId: string, name: string) => {
    if (!isPermissionEnabled) return;
    const value = nodes.filter(
      (node) => node.id === nodeId && node.level === 0
    );
    if (value.length > 0) return;
    renameLocations(nodeId, name);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingName(e.target.value);
  };

  const applySavedLocation = (
    res: any,
    savedKey: "insert_location_node" | "update_location_node"
  ) => {
    if (res.data) {
      setNodes((prevNodes) =>
        prevNodes.map((node) =>
          node.id === editingNodeId
            ? {
                ...node,
                id: res.data[savedKey].id,
                name: res.data[savedKey].name
              }
            : node
        )
      );
      return true;
    }

    if (res.errors[0].message === "Duplicate location name") {
      message.error(res.errors[0].message);
    } else {
      message.error("Something has failed. Try again later.");
    }
    setBtnDisabled(false);
    return false;
  };

  const handleNameSave = async () => {
    setBtnDisabled(true);
    const value = editingNodeId?.split("-");

    if (!editingNodeId || editingName.trim() === "") {
      return;
    }

    const { updateObj, parentID, order, id } = collectLocationUpdate(
      nodes,
      editingNodeId
    );
    const newObj = {
      name: editingName.trim(),
      parent_id: parentID,
      order
    };

    if (updateObj.location_nodes.length > 0) {
      const updateRes: any = await updateLocation({
        variables: updateObj
      });

      if (!updateRes.data) {
        message.error("Something has failed. Try again later.");
        return;
      }
    }

    // A six-segment placeholder id means the row was created client-side and
    // has never been persisted, so it needs an insert rather than a rename.
    const isNewLocation = value?.length === 6;
    const res: any = isNewLocation
      ? await insertLocation({ variables: { object: newObj } })
      : await renameLocation({
          variables: { id, update: { name: newObj.name } }
        });

    if (
      !applySavedLocation(
        res,
        isNewLocation ? "insert_location_node" : "update_location_node"
      )
    ) {
      return;
    }

    setEditingNodeId(null);
    setIsNewNode(false);
    setBtnDisabled(false);
  };

  const handleNameCancel = () => {
    if (isNewNode) {
      setNodes((prevNodes) =>
        prevNodes.filter((node) => node.id !== editingNodeId)
      );
    }
    setEditingNodeId(null);
    setIsNewNode(false);
  };

  const nodeChildrenMap = useMemo(() => {
    const map: Record<string, TreeNode[]> = {};

    nodes.forEach((node) => {
      if (!map[node.parent_id]) {
        map[node.parent_id] = [];
      }
      map[node.parent_id].push(node);
    });

    return map;
  }, [nodes]);

  const hasChildren = (nodeId: string): boolean => {
    return !!nodeChildrenMap[nodeId] && nodeChildrenMap[nodeId].length > 0;
  };

  const getVisibleNodes = (): TreeNode[] => {
    const result: TreeNode[] = [];

    const addVisibleNodes = (parentId: string) => {
      const children = nodeChildrenMap[parentId] || [];

      const sortedChildren = [...children].sort((a, b) => a.order - b.order);

      sortedChildren.forEach((node) => {
        result.push(node);
        if (expandedNodes[node.id] && hasChildren(node.id)) {
          addVisibleNodes(node.id);
        }
      });
    };

    addVisibleNodes("");

    return result;
  };

  const visibleNodes = getVisibleNodes();

  const getNodeMenu = (nodeId: string): MenuProps => {
    return {
      items: [
        {
          key: "add-sublocation",
          label: "Add Sub-Location",
          onClick: () => handleMenuAction("add-sublocation", nodeId)
        },
        {
          key: "add-before",
          label: "Add Before",
          onClick: () => handleMenuAction("add-before", nodeId)
        },
        {
          key: "add-after",
          label: "Add After",
          onClick: () => handleMenuAction("add-after", nodeId)
        },
        {
          key: "remove",
          label: (
            <span
              style={{
                color: "var(--ciq-red-700)"
              }}
            >
              Remove
            </span>
          ),
          onClick: () => handleMenuAction("remove", nodeId)
        },
        {
          type: "divider"
        },
        {
          key: "move-up",
          label: (
            <span style={{ display: "flex", alignItems: "center" }}>
              <ArrowUpOutlined
                style={{
                  marginRight: 8,
                  color: !canMoveUp(nodeId) ? "var(--ciq-gray-600)" : "black"
                }}
              />
              Move Up
            </span>
          ),
          disabled: !canMoveUp(nodeId),
          onClick: () => handleMenuAction("move-up", nodeId)
        },
        {
          key: "move-down",
          label: (
            <span style={{ display: "flex", alignItems: "center" }}>
              <ArrowDownOutlined
                style={{
                  marginRight: 8,
                  color: !canMoveDown(nodeId) ? "var(--ciq-gray-600)" : "black"
                }}
              />
              Move Down
            </span>
          ),
          disabled: !canMoveDown(nodeId),
          onClick: () => handleMenuAction("move-down", nodeId)
        },
        {
          type: "divider"
        },
        {
          key: "rename",
          label: "Rename",
          onClick: () => handleMenuAction("rename", nodeId)
        }
      ]
    };
  };

  const emptyLocationRender = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "89vh",
          backgroundColor: "white",
          paddingTop: "50px"
        }}
      >
        <Space orientation="vertical" align="center">
          <Text style={{ fontSize: "16px", color: "var(--ciq-gray-900)" }}>
            No Locations found.
          </Text>
          <Text style={{ fontSize: "14px", color: "var(--ciq-gray-700)" }}>
            Start building a Location Breakdown Structure for this Project.
          </Text>
          <Button
            type="primary"
            size="large"
            onClick={() => handleAddChild(projectId)}
            disabled={!isPermissionEnabled}
            data-testid="add-location-button"
          >
            + Add Location
          </Button>
        </Space>
      </div>
    );
  };

  return (
    <>
      <div className="w-full parent-container">
        <div className="text-[var(--ciq-gray-950)] font-bold text-xl">
          Location Breakdown Structure
        </div>
        <div className="lbs-locations-count">
          <div
            className="flex justify-center h-fit flex-wrap border border-solid border-ciq-gray-300 rounded-xl py-1 px-2 text-black text-opacity-50 text-xs font-normal"
            data-testid="location-count"
          >
            {nodes.length - 1 > 0 ? nodes.length - 1 : 0}
            &nbsp;Locations
          </div>
        </div>
      </div>
      <div className="lbs-tree-grid-container">
        <div className="lbs-tree-grid-header">
          {/* <div
          style={{
            width: "90%",
            marginLeft: "5%",
            marginRight: "5%",
            display: "flex",
            alignItems: "center",
            padding: "16px",
            border: "1px solid var(--ciq-gray-300)",
            borderRadius: "8px",
            backgroundColor: "var(--ciq-gray-50)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
          }}
        >
          <Input.TextArea
            value="Premium Residences > Tower 1 > Floor 4"
            readOnly
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{
              flexGrow: 1,
              marginRight: "16px",
              resize: "none",
              overflow: "hidden"
            }}
          />

          <Space>
            <Button>Rename</Button>
            <Button>Move Up</Button>
            <Button disabled>Move Down</Button>
            <Button danger>Remove</Button>
            <Button type="primary">+ Add Sub-Location</Button>
            <Tooltip title="This is the current location">
              <QuestionCircleOutlined
                style={{
                  fontSize: "16px",
                  marginRight: "16px",
                  color: "var(--ciq-blue-600)"
                }}
              />
            </Tooltip>
          </Space>
        </div> */}
          <div className="lbs-tier-container">
            <div className="lbs-tier-text">
              <Text>Tier</Text>
            </div>
            <div className="lbs-tier-number-container">
              {levels.map((tier) => (
                <div
                  key={tier}
                  className="lbs-tier-number"
                  onClick={() => handleTierClick(tier)}
                  data-testid={`location-tier-number-${tier}`}
                >
                  <Text>{tier}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="tree-grid">
          {/* Tree rows */}
          {visibleNodes.map((node) => (
            <div
              key={node.id}
              className="tree-grid-rows"
              data-testid={`location-tree-node-${node.id}`}
              style={{
                backgroundColor:
                  selectedNodeId === node.id ? "var(--ciq-blue-50)" : "white"
              }}
              onClick={() => handleNodeClick(node.id)}
              onMouseEnter={(e) => {
                if (selectedNodeId !== node.id) {
                  e.currentTarget.style.backgroundColor = "var(--ciq-gray-200)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedNodeId !== node.id) {
                  e.currentTarget.style.backgroundColor = "white";
                }
              }}
            >
              <div className="tree-grid-level-column">
                {node.level === 0 ? "-" : node.level}
              </div>
              {/* Node content */}
              <div
                className="tree-grid-content-column"
                style={{
                  paddingLeft: node.level === 0 ? 10 : node.level * 70
                }}
              >
                {/* Toggle button if node has children */}
                {hasChildren(node.id) ? (
                  <Button
                    type="text"
                    size="small"
                    icon={
                      node.level !== 0 &&
                      (expandedNodes[node.id] ? (
                        <DownOutlined />
                      ) : (
                        <RightOutlined />
                      ))
                    }
                    onClick={() => toggleNode(node.id)}
                    style={{ marginRight: 8, padding: 0 }}
                  />
                ) : (
                  <div style={{ width: 28, marginRight: 8 }} />
                )}

                {/* Node name - editable or display */}
                {editingNodeId === node.id ? (
                  <>
                    <div className="tree-grid-input-container">
                      <Input
                        value={editingName}
                        onChange={handleNameChange}
                        size="small"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        style={{ marginRight: 8, width: "620px" }}
                        onPressEnter={handleNameSave}
                        onKeyDown={(e: any) => {
                          if (e.key === "Escape") handleNameCancel();
                        }}
                        placeholder="Enter new location"
                        data-testid="location-name-input"
                      />
                      <Button
                        type="primary"
                        size="small"
                        onClick={handleNameSave}
                        disabled={btnDisabled}
                        // style={{ color: "#52c41a" }}
                        data-testid="save-location-name-button"
                      >
                        DONE
                      </Button>
                    </div>
                    <div className="tree-grid-remove-btn-container">
                      <Button
                        type="text"
                        size="small"
                        onClick={handleNameCancel}
                        style={{ color: "var(--ciq-red-700)", border: "none" }}
                        data-testid="cancel-location-name-button"
                      >
                        Remove
                      </Button>
                    </div>
                  </>
                ) : (
                  <Text
                    style={{
                      flex: 1,
                      fontWeight: node.level === 0 ? "bold" : "normal"
                    }}
                    data-testid="location-name-display"
                  >
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => handleNameClick(node.id, node.name)}
                    >
                      {node.name}
                    </span>
                  </Text>
                )}

                {/* Occurrences - BE work pending */}
                {node.occurrences !== undefined && !editingNodeId && (
                  <Text
                    style={{ marginRight: 4 }}
                    data-testid="location-occurrences"
                  >
                    {node.occurrences} occurrence
                    {node.occurrences !== 1 ? "s" : ""}
                  </Text>
                )}

                {/* Action buttons - only show when not editing */}
                {!editingNodeId && (
                  <Space>
                    <Button
                      type="default"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddChild(node.id)}
                      data-testid="add-sublocation-button"
                      disabled={!isPermissionEnabled}
                      title={
                        !isProjectArchived && !isPermissionEnabled
                          ? "You don't have permission to add sub-location"
                          : ""
                      }
                    />
                    {isPermissionEnabled && node.level !== 0 ? (
                      <Dropdown
                        menu={getNodeMenu(node.id)}
                        trigger={["click"]}
                        disabled={!isPermissionEnabled}
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<MoreOutlined />}
                          onClick={(e: any) => e.stopPropagation()}
                          style={{ border: "none" }}
                          data-testid="location-node-menu-button"
                        />
                      </Dropdown>
                    ) : (
                      <div style={{ paddingRight: "24px" }} />
                    )}
                  </Space>
                )}
              </div>
            </div>
          ))}

          {visibleNodes.length === 1 ? emptyLocationRender() : null}
        </div>
      </div>
    </>
  );
}

export default Locations;
